import { Anchor, Box, Button, Checkbox, Divider, Group, Stack, Text } from "@mantine/core"
import { DownloadSimpleIcon, FileTextIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { currencyFormat } from "../../utils"
import { dateTimeSecFormat } from "../../utils/date"
import { renderName } from "../../utils/render-name"
import { DetailCard, DetailField, DetailRow } from "../order/detail-card"
import { FitTable } from "../order/fit-table"
import { usePermission } from "../permission"
import type { ColumnType } from "../table"
import { ReasonTooltip } from "../ui"
import classes from "./dynamic-detail-form.module.css"

/** Figures of the sales order detail. Calculations preserved from the earlier single-column form. */
function useSalesTotals(order: any) {
  const lines = order?.lines || []
  const additional = order?.additionalLines || []

  const sub = lines.reduce((total: number, item: any) => total + (item!.quantity * item?.price || 0), 0)
  const vat = lines.reduce((total: number, item: any) => total + (item!.quantity * item?.vatAmount || 0), 0)
  const tax = lines.reduce((total: number, item: any) => total + (item!.quantity * item?.taxAmount || 0), 0)
  const lineDiscount = lines.reduce(
    (total: number, item: any) =>
      total +
      ((item?.discountType === "PERCENTAGE"
        ? (item?.price * item?.quantity * item?.discountValue) / 100
        : item?.quantity * item?.discountValue) || 0),
    0,
  )
  const additionalSub = additional.reduce((total: number, item: any) => total + (item!.quantity * item?.price || 0), 0)
  const additionalDisc = additional.reduce(
    (total: number, item: any) =>
      total +
      ((item?.discountType === "PERCENTAGE"
        ? ((item?.price || 0) * (item?.quantity || 0) * item.discountValue) / 100
        : item?.discountValue) || 0),
    0,
  )
  const shipping = order?.shippingAmount || 0
  const additionalTotal = additional.length ? additionalSub - additionalDisc : 0

  // The order-level figures come STRAIGHT OFF THE ORDER — they are not
  // recomputed here. The old base added `vat + tax` on top of line totals that
  // are already VAT-INCLUSIVE and put the shipping fee inside the percentage,
  // so the supplier and the buyer were shown two different totals for one order.
  //
  // de-order-service order/create.ts builds ORDER_AMOUNT from lines plus
  // additional lines (line 553-556), applies the discount to THAT (line 573) and
  // adds shipping AFTER (line 621), persisting `orderAmount` (pre-discount) and
  // `totalAmount` (post-discount, shipping in). So the grand total IS
  // `totalAmount`, and the deduction is what the two headers differ by once the
  // shipping fee is taken back out of the net side.
  const orderAmount = order?.orderAmount || 0
  const totalAmount = order?.totalAmount || 0
  const orderLevelDisc = Math.max(0, orderAmount - totalAmount + shipping)
  const grandTotal = lines.length ? totalAmount : 0

  const totalQty = lines.reduce((total: number, item: any) => total + (item!.quantity || 0), 0)

  // The COD advance is a share of the grand total, so it has to be built from the
  // corrected one — the supplier was being told to collect an advance on a total
  // the order never had.
  const codAdvance =
    order?.paymentTerm?.condition === "COD"
      ? (grandTotal * (order?.paymentTerm?.advancePercent || 0)) / 100
      : null

  return { lines, additional, sub, vat, tax, lineDiscount, shipping, additionalTotal, orderLevelDisc, grandTotal, totalQty, codAdvance }
}

/**
 * «Захиалгын зүйлс (N)» card of Ord_SalesDetail: split / dropship actions and a compact line table that fits the column.
 * The canvas table has no checkbox column; it appears only after «Захиалга хуваах» or «Дропшиппинг захиалга»
 * is pressed, with «Болих» / «Үргэлжлүүлэх (n)» in place of the two buttons.
 */
export function SupplierSalesLinesCard({
  order,
  checked,
  setChecked,
  onSubmit: _onSubmit,
  onSplit: _onSplit,
}: {
  order: any
  checked: any
  setChecked: any
  onSubmit: (value: any) => void
  onSplit: (value: any) => void
}) {
  const { t } = useTranslation(["supplierSales", "common"])
  const ORD_SPLIT = usePermission("ORD_SPLIT")
  const ORD_DRSHP = usePermission("ORD_DRSHP")
  const [picking, setPicking] = useState<"split" | "dropship" | null>(null)

  const columns = useHeader({
    t,
    picking: !!picking,
    checked,
    onClick: (key: string, record: any) => {
      if (key === "select_all") setChecked(record)
      if (key === "unselect_all") setChecked([])
      if (key === "select") {
        if (checked.some((item: any) => item.id === record.id)) {
          setChecked([...checked.filter((item: any) => item.id !== record.id)])
        } else setChecked([...checked, record])
      }
    },
  })

  // Both actions stay visible; the reason is the permission or the status.
  const statusReason = order?.orderStatus !== "AUTHORIZED" ? t("supplierSales:sales.needsAuthorized") : null
  const splitReason = ORD_SPLIT.reason ?? statusReason
  const dropshipReason = ORD_DRSHP.reason ?? statusReason

  // A fresh selection each time: a modal closed with its own × keeps the old one otherwise.
  const startPicking = (mode: "split" | "dropship") => {
    setChecked([])
    setPicking(mode)
  }
  const stopPicking = () => {
    setPicking(null)
    setChecked([])
  }
  const continuePicking = () => {
    const mode = picking
    setPicking(null)
    if (mode === "split") _onSplit(checked)
    else if (mode === "dropship") _onSubmit(checked)
  }

  const lines = order?.lines || []
  const hasNotes = !!(order?.senderNote || order?.senderAdditionalNote)

  return (
    <DetailCard
      flush
      title={`${t("supplierSales:sales.orderItems")} (${lines.length})`}
      extra={
        order?.salesType !== "SPLIT" &&
        (picking ? (
          <Group gap="xs" wrap="nowrap">
            <Button variant="default" onClick={stopPicking}>
              {t("common:confirm.cancel")}
            </Button>
            <ReasonTooltip reason={checked?.length ? null : t("supplierSales:sales.selectLinesFirst")}>
              <Button color="dark" onClick={continuePicking}>
                {t("supplierSales:sales.continueWith", { count: checked?.length || 0 })}
              </Button>
            </ReasonTooltip>
          </Group>
        ) : (
          <Group gap="xs" wrap="nowrap">
            <ReasonTooltip reason={splitReason}>
              <Button variant="default" onClick={() => startPicking("split")}>
                {t("supplierSales:sales.splitOrder")}
              </Button>
            </ReasonTooltip>
            <ReasonTooltip reason={dropshipReason}>
              <Button variant="default" onClick={() => startPicking("dropship")}>
                {t("supplierSales:sales.dropshippingOrder")}
              </Button>
            </ReasonTooltip>
          </Group>
        ))
      }>
      {picking && (
        <Text className={classes.pickHint}>
          {t(picking === "split" ? "supplierSales:sales.pickSplitLines" : "supplierSales:sales.pickDropshipLines")}
        </Text>
      )}
      <FitTable columns={columns} rows={lines} rowKey={(row: any, i) => row?.id ?? String(i)} empty={t("common:table.noData", { defaultValue: "Мэдээлэл байхгүй" })} />

      {hasNotes && (
        <Stack gap="sm" className={classes.notes}>
          {order?.senderNote && (
            <DetailField label={t("supplierSales:sales.customerAdditionalNote")}>{order.senderNote}</DetailField>
          )}
          {order?.senderAdditionalNote && (
            <DetailField label={t("supplierSales:sales.enterNote")}>{order.senderAdditionalNote}</DetailField>
          )}
        </Stack>
      )}
    </DetailCard>
  )
}

/** Right column of Ord_SalesDetail: payment amount, summary, additional lines, attachments, payment confirmation. */
export function SupplierSalesSummaryColumn({ order }: { order: any }) {
  const { t } = useTranslation(["supplierSales", "common"])
  const now_date = new Date()
  const totals = useSalesTotals(order)
  const { additional } = totals
  const attachments = order?.attachments || []
  const isCod = order?.paymentTerm?.condition === "COD"

  // B.5 — on a buyer's order the order-level discount is the payment-term rule the
  // server applied, so name the term in the label; the supplier sees at a glance
  // why the total dropped. Plain label when there is no discount or no term.
  const priceDiscountLabel =
    order?.discountType && order?.paymentTerm?.description
      ? `${t("supplierSales:sales.priceDiscount")} (${order?.paymentTerm?.description})`
      : t("supplierSales:sales.priceDiscount")

  return (
    <Stack gap="md">
      <DetailCard title={t("supplierSales:sales.paymentAmount", { defaultValue: "Төлбөрийн дүн" })}>
        <Stack gap="sm">
          <DetailRow label={t("supplierSales:sales.orderTotalAmount")} mono>{currencyFormat(totals.sub)}</DetailRow>
          <DetailRow label={t("supplierSales:sales.dVat")} mono>{currencyFormat(totals.vat)}</DetailRow>
          <DetailRow label={t("supplierSales:sales.dTax")} mono>{currencyFormat(totals.tax)}</DetailRow>
          <DetailRow label={t("supplierSales:sales.dDiscount")} mono>
            {totals.lineDiscount ? `−${currencyFormat(totals.lineDiscount)}` : currencyFormat(0)}
          </DetailRow>
          <DetailRow label={t("supplierSales:sales.shippingFee")} mono>{currencyFormat(totals.shipping)}</DetailRow>
          <DetailRow label={priceDiscountLabel} mono>
            {/* Money first, rate as a suffix: it is the number that explains the drop in the grand total. */}
            {order?.discountType
              ? `−${currencyFormat(totals.orderLevelDisc)}${order?.discountType === "PERCENTAGE" ? ` (${order?.discountValue || 0}%)` : ""}`
              : "—"}
          </DetailRow>
          {additional.length > 0 && (
            <DetailRow label={t("supplierSales:sales.additionalTotalAmount")} mono>{currencyFormat(totals.additionalTotal)}</DetailRow>
          )}
          <Divider color="gray.3" my={4} />
          <Group justify="space-between" wrap="nowrap" gap="md">
            <Text className={classes.grandLabel}>{t("supplierSales:sales.grandTotal")}</Text>
            <Text ff="monospace" className={classes.grandValue}>{currencyFormat(totals.grandTotal)}</Text>
          </Group>
        </Stack>
      </DetailCard>

      <DetailCard title={t("supplierSales:sales.orderSummary")}>
        <Stack gap="xs">
          <DetailRow label={t("supplierSales:sales.totalTypes", { defaultValue: "Нийт төрөл" })} mono>{totals.lines.length}</DetailRow>
          <DetailRow label={t("supplierSales:sales.totalQuantity")} mono>
            {`${totals.totalQty} ${t("supplierSales:sales.piece")}`}
          </DetailRow>
        </Stack>
      </DetailCard>

      <DetailCard title={t("supplierSales:sales.additionalItems")}>
        {additional.length === 0 ? (
          <Text className={classes.muted}>-</Text>
        ) : (
          <Stack gap="sm">
            {additional.map((item: any, i: number) => {
              const gross = (item?.price || 0) * (item?.quantity || 0)
              const disc = item?.discountType === "PERCENTAGE" ? (gross * (item?.discountValue || 0)) / 100 : item?.discountValue || 0
              const discText =
                item?.discountType === "PERCENTAGE"
                  ? `${item?.discountValue}%`
                  : item?.discountType === "AMOUNT"
                  ? currencyFormat(item?.discountValue || 0)
                  : null
              return (
                <Group key={item?.id ?? i} justify="space-between" wrap="nowrap" gap="md" align="flex-start">
                  <Box miw={0}>
                    <Text className={classes.itemName}>{item?.name ?? "-"}</Text>
                    <Text className={classes.muted}>
                      <Text span ff="monospace" inherit>{`${item?.quantity ?? 0} × ${currencyFormat(item?.price || 0)}`}</Text>
                      {item?.unit ? ` · ${item.unit}` : ""}
                      {discText ? ` · ${t("supplierSales:sales.discount")} ${discText}` : ""}
                    </Text>
                  </Box>
                  <Text ff="monospace" className={classes.amount}>{currencyFormat(gross - disc || 0)}</Text>
                </Group>
              )
            })}
          </Stack>
        )}
      </DetailCard>

      <DetailCard title={t("supplierSales:sales.attachment")}>
        {attachments.length === 0 ? (
          <Stack gap={6} align="center" py="sm">
            <FileTextIcon size={20} color="var(--mantine-color-gray-7)" />
            <Text className={classes.muted}>{t("common:table.noData", { defaultValue: "Мэдээлэл байхгүй" })}</Text>
          </Stack>
        ) : (
          <Stack gap="sm">
            {attachments.map((file: any, i: number) => (
              <Box key={file?.id ?? i}>
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Text className={classes.itemName} truncate>{file?.name ?? "-"}</Text>
                  {file?.url && (
                    <Anchor href={file.url} target="_blank" rel="noopener noreferrer" download size="sm" c="dark.7">
                      <Group gap={4} wrap="nowrap">
                        <DownloadSimpleIcon size={14} />
                        {t("supplierSales:sales.download", { defaultValue: "Татах" })}
                      </Group>
                    </Anchor>
                  )}
                </Group>
                <Text className={classes.muted}>
                  {[dateTimeSecFormat(file?.createdAt), renderName(file?.attachedUser), file?.description].filter(Boolean).join(" · ")}
                </Text>
              </Box>
            ))}
          </Stack>
        )}
      </DetailCard>

      <DetailCard title={t("supplierSales:sales.paymentConfirmation")}>
        {isCod ? (
          <Stack gap="xs">
            <DetailRow label={t("supplierSales:sales.paymentConfirmation")} mono>{currencyFormat(totals.codAdvance || 0)}</DetailRow>
            <DetailRow label={t("supplierSales:sales.paymentConfirmationDate")} mono>
              {dateTimeSecFormat(now_date.setDate(now_date.getDate() + (order?.paymentTerm?.confirmationDay || 0)))}
            </DetailRow>
          </Stack>
        ) : (
          <Text className={classes.muted}>-</Text>
        )}
      </DetailCard>
    </Stack>
  )
}

const money = (value: number) => (
  <Text ff="monospace" size="sm" c="dark.7" className={classes.nowrap}>
    {currencyFormat(value)}
  </Text>
)

const useHeader = ({
  t,
  onClick,
  checked,
  picking,
}: {
  t: any
  onClick: (key: string, record: any) => void
  checked: string[]
  /** Selection mode for split / dropship: only then the checkbox column is shown. */
  picking: boolean
}): ColumnType<any>[] => [
  ...(picking ? [{
    title: "",
    // Widths include the cell padding (fixed layout, border-box); the first cell also has the card gutter.
    width: 52,
    renderCheck(record: any) {
      return (
        <Checkbox
          aria-label={t("supplierSales:sales.selectedProducts")}
          onChange={() => { }}
          onClick={() => {
            if (Array.isArray(record?.rows) && Array.isArray(checked) && record?.rows.length === checked.length) {
              onClick("unselect_all", record?.rows)
            } else onClick("select_all", record?.rows)
          }}
          checked={checked?.length === record?.rows?.length}
        />
      )
    },
    render: (record) => (
      <Checkbox
        aria-label={record?.skuCode ?? ""}
        onChange={() => { }}
        onClick={() => onClick("select", record)}
        checked={checked.some((item: any) => item.id === record.id)}
      />
    ),
  } as ColumnType<any>] : []),
  {
    title: "#",
    // As the first column it also carries the card gutter (24px left padding).
    width: picking ? 36 : 52,
    render: (_r, index) => <Text size="sm" c="dark.6">{index + 1}</Text>,
  },
  {
    title: t("supplierSales:sales.skuCode"),
    width: 150,
    render: (record) => (
      <Text ff="monospace" size="sm" c="dark.7" truncate title={record?.skuCode ?? undefined}>
        {record?.skuCode ?? "-"}
      </Text>
    ),
  },
  {
    title: t("supplierSales:sales.colItem"),
    render: (record) => {
      const discount =
        record?.discountType === "PERCENTAGE"
          ? ((record?.price || 0) * (record?.quantity || 0) * record.discountValue) / 100
          : (record?.quantity || 0) * (record?.discountValue || 0)
      const taxTotal = (record?.taxAmount || 0) * (record?.quantity || 0)
      // Details that had their own columns before the canvas; shown only when present.
      const meta = [
        discount ? `${t("supplierSales:sales.discount")} −${currencyFormat(discount)}` : null,
        taxTotal ? `${t("supplierSales:sales.tax")} ${currencyFormat(taxTotal)}` : null,
        record?.isDropshipping ? t("supplierSales:sales.dropshipping") : null,
        record?.childOrderSalesCode ? `${t("supplierSales:sales.subSO")} ${record.childOrderSalesCode}` : null,
      ].filter(Boolean)
      return (
        <Box>
          <Text size="sm" c="dark.8" lh={1.35}>{record?.name ?? "-"}</Text>
          {meta.length > 0 && <Text size="sm" c="gray.8">{meta.join(" · ")}</Text>}
        </Box>
      )
    },
  },
  {
    title: t("supplierSales:sales.colUnit"),
    width: 80,
    render: (record) => <Text size="sm" c="dark.6" truncate>{record?.unit ?? "-"}</Text>,
  },
  {
    title: t("supplierSales:sales.colPrice"),
    width: 104,
    align: "right",
    render: (record) => money(record?.price || 0),
  },
  {
    title: t("supplierSales:sales.colQty"),
    width: 60,
    align: "right",
    render: (record) => <Text ff="monospace" size="sm" c="dark.7">{record?.quantity ?? 0}</Text>,
  },
  {
    title: t("supplierSales:sales.vat"),
    width: 96,
    align: "right",
    render: (record) => money((record?.vatAmount || 0) * (record?.quantity || 0)),
  },
  {
    title: t("supplierSales:sales.colTotal"),
    width: 124,
    align: "right",
    render: (record: any) => (
      <Text ff="monospace" size="sm" fw={700} c="dark.8" className={classes.nowrap}>
        {currencyFormat(record?.totalAmount ?? 0)}
      </Text>
    ),
  },
]
